// Invoice detail page — header + per-line breakdown for one uploaded invoice.
// Routes: /admin/auspost/invoices/:invoiceId
//
// The line table shows what the reconciliation found per parcel: SS cost,
// AP invoice amount, the variance, and the current review status.

import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Loader2, ArrowLeft, FileText, Calendar, AlertCircle,
} from 'lucide-react'
import { getAuspostInvoice } from '../../lib/api'
import AuspostTabs from './AuspostTabs'


function fmt$(v) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency', currency: 'AUD', minimumFractionDigits: 2,
  }).format(v ?? 0)
}
function fmtDate(s) {
  if (!s) return '—'
  const d = new Date(s)
  return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
  })
}
function fmtDateTime(s) {
  if (!s) return '—'
  const d = new Date(s)
  return isNaN(d.getTime()) ? s : d.toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}


export default function AuspostInvoiceDetail() {
  const { invoiceId } = useParams()
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['auspost', 'invoice', invoiceId],
    // Ask for the whole invoice: the filter-chip counts are computed
    // client-side over whatever comes back, so a truncated response
    // silently under-reports Unmatched / Return labels / Discrepancy.
    queryFn:  () => getAuspostInvoice(invoiceId, { limit: 10000 }),
    enabled:  !!invoiceId,
  })

  const [filter, setFilter] = useState('all')

  const lines = data?.lines || []
  const filtered = useMemo(() => {
    if (filter === 'all') return lines
    if (filter === 'matched') return lines.filter(l => l.match_source === 'order')
    if (filter === 'returns') return lines.filter(l => l.match_source === 'return_label')
    if (filter === 'siblings') return lines.filter(l => l.match_source === 'sibling')
    if (filter === 'unmatched') return lines.filter(l => !l.match_source)
    if (filter === 'discrepancy') {
      return lines.filter(l => {
        if (l.ss_carrier_cost == null || l.amount_inc_gst == null) return false
        return Math.abs((l.amount_inc_gst ?? 0) - (l.ss_carrier_cost ?? 0)) > 0.05
      })
    }
    if (filter === 'pending') return lines.filter(l => l.review_status === 'pending' || !l.review_status)
    return lines
  }, [lines, filter])

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-800">AusPost Reconciliation</h2>
        <p className="text-sm text-slate-500 mt-0.5">Per-line invoice breakdown.</p>
      </div>

      <AuspostTabs />

      <Link
        to="/admin/auspost/invoices"
        className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800"
      >
        <ArrowLeft size={12} /> Back to all uploads
      </Link>

      {isLoading ? (
        <div className="card p-10 flex items-center justify-center gap-2 text-slate-400">
          <Loader2 size={16} className="animate-spin" /> Loading invoice…
        </div>
      ) : isError ? (
        <div className="card p-6 text-sm text-red-600">
          Failed to load invoice: {error?.response?.data?.detail || error?.message || 'Unknown error'}
        </div>
      ) : !data ? null : (
        <>
          <HeaderCard invoice={data.invoice} totalLines={data.total_lines} />
          <FilterBar filter={filter} setFilter={setFilter} lines={lines} />
          <LinesTable lines={filtered} totalLines={data.total_lines} shownAll={lines.length === data.total_lines} />
        </>
      )}
    </div>
  )
}


// ─── Header card ───────────────────────────────────────────────────────────

function HeaderCard({ invoice, totalLines }) {
  const c = invoice.recon_counts || {}
  const totals = [
    { label: 'Total inc-GST',  value: fmt$(invoice.total_amount_inc_gst) },
    { label: 'Total ex-GST',   value: fmt$(invoice.total_amount_ex_gst) },
    { label: 'GST',            value: fmt$(invoice.total_gst) },
    { label: 'Lines (article)',value: `${invoice.line_count_with_article ?? 0} / ${invoice.line_count_total ?? 0}` },
  ]
  const outcomes = [
    { label: 'Charged (open period)',  value: c.charged_open_period ?? 0, color: 'text-emerald-600' },
    { label: 'Deferred to current',     value: c.discrepancies_filed ?? 0, color: 'text-amber-600' },
    { label: 'Pending review',          value: c.pending_review ?? 0,      color: 'text-red-600' },
    { label: 'Already at target',       value: c.unchanged ?? 0,           color: 'text-slate-500' },
    { label: 'No SS match',             value: c.no_ss_match ?? 0,         color: 'text-slate-500' },
  ]
  return (
    <div className="card overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
        <div className="w-10 h-10 rounded bg-blue-50 flex items-center justify-center">
          <FileText size={18} className="text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono font-semibold text-slate-800">#{invoice.invoice_number}</div>
          <div className="text-xs text-slate-500">
            {invoice.source_type?.toUpperCase()}
            {invoice.billing_date && <> · billed {fmtDate(invoice.billing_date)}</>}
            {invoice.period_end_date && <> · period ends {fmtDate(invoice.period_end_date)}</>}
            <span className="ml-2 text-slate-400">· uploaded {fmtDateTime(invoice.uploaded_at)}</span>
          </div>
        </div>
      </div>

      <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-4 gap-4">
        {totals.map((t) => (
          <div key={t.label}>
            <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{t.label}</div>
            <div className="text-base font-semibold text-slate-800 tabular-nums">{t.value}</div>
          </div>
        ))}
      </div>

      <div className="px-5 py-4 border-t border-slate-100 bg-slate-50/60">
        <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Recon outcome</div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
          {outcomes.map((o) => (
            <div key={o.label} className={`flex items-center gap-1.5 ${o.color}`}>
              <span className="font-semibold tabular-nums">{o.value}</span>
              <span className="text-slate-500 text-xs">{o.label}</span>
            </div>
          ))}
        </div>
      </div>

      {invoice.parser_warnings?.length > 0 && (
        <div className="px-5 py-3 border-t border-slate-100 bg-amber-50/70 text-xs text-amber-800 flex items-start gap-2">
          <AlertCircle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-medium">Parser notes</div>
            <ul className="list-disc ml-4">
              {invoice.parser_warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}


// Variance for one invoice line, consignment-aware.
//
// AusPost bills each parcel of a multi-parcel consignment as its own
// article, while ShipStation quotes the whole consignment on a single
// order. Comparing one line against the full order cost pits 1/N of the
// carrier charge against 100% of the price — a 5-parcel shipment quoted
// at $94.19 rendered as a bogus -$75.76 credit. So for multi-parcel
// consignments compare the consignment total, and only on the row that
// actually carries the ShipStation order; the other parcels have nothing
// meaningful of their own to compare.
//
// Shared by the row renderer and the Discrepancy chip so the count and
// the table can never disagree.
function lineVariance(line) {
  const isMulti = (line.consignment_parcels ?? 1) > 1
  if (isMulti && line.match_source !== 'order') return null
  const ap = isMulti ? line.consignment_ap_total : line.amount_inc_gst
  if (line.ss_carrier_cost == null || ap == null) return null
  return ap - line.ss_carrier_cost
}


// ─── Filter bar ────────────────────────────────────────────────────────────

function FilterBar({ filter, setFilter, lines }) {
  const counts = useMemo(() => ({
    all:         lines.length,
    matched:     lines.filter(l => l.match_source === 'order').length,
    returns:     lines.filter(l => l.match_source === 'return_label').length,
    siblings:    lines.filter(l => l.match_source === 'sibling').length,
    // "Unmatched" now means genuinely unaccounted for — return labels and
    // sibling parcels have their own chips rather than being lumped in here.
    unmatched:   lines.filter(l => !l.match_source).length,
    discrepancy: lines.filter(l => lineVariance(l) != null && Math.abs(lineVariance(l)) > 0.05).length,
    pending:     lines.filter(l => l.review_status === 'pending' || !l.review_status).length,
  }), [lines])
  const chips = [
    { key: 'all',         label: 'All' },
    { key: 'matched',     label: 'SS matched' },
    { key: 'returns',     label: 'Return labels' },
    { key: 'siblings',    label: 'Extra parcels' },
    { key: 'unmatched',   label: 'Unmatched' },
    { key: 'discrepancy', label: 'Discrepancy' },
    { key: 'pending',     label: 'Pending review' },
  ]
  return (
    <div className="flex flex-wrap items-center gap-2">
      {chips.map((c) => (
        <button
          key={c.key}
          onClick={() => setFilter(c.key)}
          className={[
            'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors',
            filter === c.key
              ? 'bg-blue-100 text-blue-700 border border-blue-200'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50',
          ].join(' ')}
        >
          {c.label}
          <span className="text-[10px] opacity-60 tabular-nums">{counts[c.key]}</span>
        </button>
      ))}
    </div>
  )
}


// ─── Lines table ───────────────────────────────────────────────────────────

function LinesTable({ lines, totalLines, shownAll }) {
  const PAGE = 100
  const [page, setPage] = useState(1)
  const start = (page - 1) * PAGE
  const end   = Math.min(start + PAGE, lines.length)
  const paged = lines.slice(start, end)
  const totalPages = Math.max(1, Math.ceil(lines.length / PAGE))
  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500 uppercase tracking-wider">
            <tr>
              <th className="px-4 py-2.5 text-left">SS order</th>
              <th className="px-4 py-2.5 text-left">Article / Consignment</th>
              <th className="px-4 py-2.5 text-left">Product</th>
              <th className="px-4 py-2.5 text-right">SS cost</th>
              <th className="px-4 py-2.5 text-right">AP invoice (inc-GST)</th>
              <th className="px-4 py-2.5 text-right">Variance</th>
              <th className="px-4 py-2.5 text-left">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {paged.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-400">
                  No lines match this filter.
                </td>
              </tr>
            ) : (
              paged.map((l) => <LineRow key={l.id} line={l} />)
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100 bg-slate-50 text-xs text-slate-600">
        <div>
          Showing {start + 1}–{end} of {lines.length}
          {!shownAll && (
            <span className="text-slate-400 ml-2">
              · server returned {lines.length} of {totalLines} (cap 1000)
            </span>
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-2 py-1 rounded border border-slate-300 bg-white disabled:opacity-40"
            >
              Prev
            </button>
            <span className="tabular-nums">{page} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-2 py-1 rounded border border-slate-300 bg-white disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function LineRow({ line }) {
  const isMultiParcel = (line.consignment_parcels ?? 1) > 1
  const variance = lineVariance(line)
  const varianceColor =
    variance == null ? 'text-slate-400' :
    Math.abs(variance) <= 0.05 ? 'text-slate-400' :
    variance > 0 ? 'text-red-600' : 'text-emerald-600'

  const statusBadge = (() => {
    const s = line.review_status
    if (s === 'approved') return <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">approved</span>
    if (s === 'disputed') return <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50  text-amber-700">disputed</span>
    if (s === 'written_off') return <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">written off</span>
    if (s === 'pending') return <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-50 text-orange-700">pending</span>
    return <span className="text-[10px] text-slate-400">—</span>
  })()

  return (
    <tr className="hover:bg-slate-50">
      <td className="px-4 py-2 font-mono text-xs">
        {line.ss_order_number ? (
          <>
            <div className="text-slate-700">{line.ss_order_number}</div>
            {line.match_source === 'return_label' && (
              <div className="text-[10px] text-indigo-600 font-sans">
                return label
                {line.return_label_voided && (
                  <span className="ml-1 text-amber-600">· voided</span>
                )}
              </div>
            )}
          </>
        ) : line.match_source === 'sibling' ? (
          <span
            className="text-[11px] text-slate-500 font-sans italic"
            title={`Extra parcel in the same consignment as ${line.sibling_of_article}. Its cost is attributed to that parcel's ShipStation order.`}
          >
            extra parcel in consignment
          </span>
        ) : (
          <span className="text-slate-400 italic">no match</span>
        )}
      </td>
      <td className="px-4 py-2 text-xs">
        <div className="font-mono text-slate-700 truncate max-w-[24ch]" title={line.article_id || ''}>
          {line.article_id || '—'}
        </div>
        {line.consignment_id && (
          <div className="text-[10px] text-slate-400 font-mono">{line.consignment_id}</div>
        )}
      </td>
      <td className="px-4 py-2 text-xs text-slate-600">
        <div>{line.description || '—'}</div>
        {line.charge_code && (
          <div className="text-[10px] text-slate-400">{line.charge_code} · qty {line.qty ?? 1}</div>
        )}
      </td>
      <td className="px-4 py-2 text-right tabular-nums text-xs text-slate-700">
        {line.ss_carrier_cost == null ? <span className="text-slate-400">—</span> : fmt$(line.ss_carrier_cost)}
      </td>
      <td className="px-4 py-2 text-right tabular-nums text-xs text-slate-700">
        {fmt$(line.amount_inc_gst)}
        {isMultiParcel && (
          <div
            className="text-[10px] text-slate-400 font-sans"
            title={`AusPost billed this consignment as ${line.consignment_parcels} separate parcels totalling ${fmt$(line.consignment_ap_total)}. ShipStation quotes the whole consignment on one order, so the variance is measured against that total.`}
          >
            {line.match_source === 'order'
              ? `${fmt$(line.consignment_ap_total)} across ${line.consignment_parcels} parcels`
              : `1 of ${line.consignment_parcels} parcels`}
          </div>
        )}
      </td>
      <td className={`px-4 py-2 text-right tabular-nums text-xs font-medium ${varianceColor}`}>
        {variance == null ? '—' : (Math.abs(variance) <= 0.05 ? fmt$(0) : fmt$(variance))}
      </td>
      <td className="px-4 py-2">{statusBadge}</td>
    </tr>
  )
}
